import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Navigate, useOutletContext } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Panel, Kpi } from "./OfficeLayout";
import { api, money, titleize } from "../../lib/api";

export const PerfTable = ({ rows, testId }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-xs">
      <thead><tr className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
        {["Source", "Scans", "Clicks", "Leads", "Completed", "Qualified", "Handoffs", "Conv.", "Cost", "CPL", "Conv %"].map((h) => <th key={h} className="whitespace-nowrap px-2 py-1.5">{h}</th>)}
      </tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.key} className="border-t border-border">
          <td className="max-w-[220px] truncate px-2 py-1.5 font-medium">{r.label}</td><td className="px-2 font-mono">{r.scans}</td><td className="px-2 font-mono">{r.clicks}</td>
          <td className="px-2 font-mono">{r.intakes_started}</td><td className="px-2 font-mono">{r.intakes_completed}</td><td className="px-2 font-mono">{r.qualified}</td>
          <td className="px-2 font-mono">{r.handoffs}</td><td className="px-2 font-mono">{r.conversions}</td><td className="px-2 font-mono">{money(r.cost)}</td>
          <td className="px-2 font-mono">{money(r.cost_per_lead)}</td><td className="px-2 font-mono">{r.conversion_rate}%</td>
        </tr>))}
        {!rows.length && <tr><td colSpan={11} className="px-2 py-4 text-center text-muted-foreground">No data yet</td></tr>}
      </tbody>
    </table>
  </div>
);

export default function Dashboard() {
  const { user } = useOutletContext();
  return user.role === "admin" ? <AdminDashboard /> : <Navigate to="/office/leads" replace />;
}

function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/office/dashboard").then(({ data }) => setD(data)); }, []);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const m = d.metrics, c = d.counts;
  const kpis = [["Total traffic", m.visits], ["QR scans", m.scans], ["Link clicks", m.clicks], ["New leads", m.intakes_started], ["Qualified leads", m.qualified],
    ["Urgent leads", c.urgent, "text-red-400"], ["Foreclosure leads", c.foreclosure], ["Tax-deed leads", c.tax_deed], ["Urgent-sale leads", c.urgent_sale, "text-orange-300"],
    ["Keep-home leads", c.keep_home], ["Sell-home leads", c.sell_home], ["Completed intakes", m.intakes_completed], ["Incomplete intakes", c.incomplete],
    ["Specialist assignments", c.specialist_assigned], ["Conversions", m.conversions, "text-emerald-400"], ["Closed cases", m.closed],
    ["Marketing cost", money(m.cost)], ["Cost per lead", money(m.cost_per_lead)], ["Cost / qualified lead", money(m.cost_per_qualified)],
    ["Conversion rate", `${m.conversion_rate}%`], ["Revenue attribution", money(m.revenue)], ["Possible duplicates", c.duplicates, "text-amber-300"], ["Scam flags", c.scam_flags, "text-red-400"]];
  return (
    <div className="space-y-4" data-testid="admin-dashboard">
      <div><p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Executive overview</p><h1 className="text-2xl font-bold">AskJoe-AI Back Office</h1></div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-6">{kpis.map(([l, v, a]) => <Kpi key={l} l={l} v={v} accent={a} testId={`kpi-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} />)}</div>
      <div className="grid gap-4 xl:grid-cols-[2fr_1fr]">
        <Panel title="Traffic & leads — last 30 days" testId="traffic-chart">
          <div className="h-56"><ResponsiveContainer>
            <AreaChart data={d.series}><CartesianGrid stroke="#1F2937" vertical={false} /><XAxis dataKey="date" stroke="#6B7280" fontSize={10} /><YAxis stroke="#6B7280" fontSize={10} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937" }} />
              <Area dataKey="visits" stroke="#06B6D4" fill="#06B6D4" fillOpacity={0.15} /><Area dataKey="leads" stroke="#10B981" fill="#10B981" fillOpacity={0.25} /></AreaChart>
          </ResponsiveContainer></div>
        </Panel>
        <Panel title="Lead pipeline" testId="pipeline-panel">
          <div className="max-h-56 space-y-1 overflow-y-auto">{Object.entries(d.pipeline).map(([s, n]) => (
            <div key={s} className="flex items-center gap-2 text-xs"><span className="w-40 shrink-0 truncate text-muted-foreground">{titleize(s)}</span>
              <div className="h-1.5 flex-1 rounded bg-secondary"><div className="h-1.5 rounded bg-primary" style={{ width: `${Math.min(100, n * 20)}%` }} /></div><span className="w-6 text-right font-mono">{n}</span></div>
          ))}</div>
        </Panel>
      </div>
      <Panel title="Referral partner & team member performance"><PerfTable rows={d.by_partner} testId="perf-partners" /></Panel>
      <Panel title="Campaign performance"><PerfTable rows={d.by_campaign} testId="perf-campaigns" /></Panel>
      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title="ZIP-code performance"><PerfTable rows={d.by_zip} testId="perf-zip" /></Panel>
        <Panel title="City performance"><PerfTable rows={d.by_city} testId="perf-city" /></Panel>
      </div>
    </div>
  );
}
